import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import PublishedWithChangesOutlinedIcon from '@mui/icons-material/PublishedWithChangesOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { MenuItem, Divider } from '@mui/material'
import { RedactionWorkbench, RedactionViewer, UnsavedChangesDialog, TOOLBAR_HEIGHT } from '@standalone-ai/redaction-workbench'
import { api } from '../api/client.js'
import { pdfjs } from '../api/pdfjs.js'
import { errorMessage } from '../api/errors.js'
import { downloadFile, downloadRedactionPackage } from '../api/downloads.js'
import { streamOperation } from '../api/operations.js'
import { loadLimits } from '../api/limits.js'
import { makeRedactionPorts } from '../api/redactionPorts.js'
import { exportAnnotated, importAnnotated } from '../api/redactionResults.js'
import { useUnsavedGuard } from '../hooks/useUnsavedGuard.js'
import { tokens } from '../theme.js'
import { SecondaryButton, SecondaryIconButton, Spinner, KindBadge, ErrorText, ActionMenu } from '../components/styles/index.js'
import { ApplyResultDialog } from '../components/common/dialogs.js'
import { displayKind } from '../components/files/fileStatus.js'
import { folderPath } from '../components/files/FolderTree.js'

const VIEW_ONLY = {
  annotated: { label: 'Annotated PDF', info: 'Review copy. Boxes are drawn over the text; nothing has been removed.' },
  redacted: { label: 'Redacted PDF', info: 'Redactions are burned into this file; the covered content is gone.' },
  reasons: { label: 'Summary', info: 'Lists every burned box with its reason.' }
}

export default function EditorPage() {
  const { id, fileId } = useParams()
  const [params] = useSearchParams()
  const location = useLocation()
  const artifact = params.get('artifact')
  const navigate = useNavigate()
  const [project, setProject] = useState(null)
  const [folders, setFolders] = useState([])
  const [file, setFile] = useState(null)
  const [error, setError] = useState('')
  const [dirty, setDirty] = useState(false)
  const [changes, setChanges] = useState(null)
  const [applying, setApplying] = useState(false)
  const [menu, setMenu] = useState(null) // { top, left } while the "…" menu is open
  const [working, setWorking] = useState('') // 'export' | 'import' | 'summary' while one runs
  const [applied, setApplied] = useState(null)
  const [downloadState, setDownloadState] = useState('')
  const [downloadError, setDownloadError] = useState('')
  const [limits, setLimits] = useState(null) // the server's caps; the package defaults until they arrive
  const wb = useRef(null)
  const importInput = useRef(null)
  const ports = useMemo(() => makeRedactionPorts(fileId, artifact || 'pdf'), [fileId, artifact])

  useEffect(() => {
    api
      .get(`/api/projects/${id}`)
      .then(r => setProject(r.data))
      .catch(() => navigate('/', { replace: true }))
    api
      .get(`/api/projects/${id}/folders`)
      .then(r => setFolders(r.data))
      .catch(() => {})
  }, [id, navigate])
  useEffect(() => {
    api
      .get(`/api/files/${fileId}`)
      .then(r => setFile(r.data))
      .catch(e => setError(errorMessage(e)))
  }, [fileId])
  useEffect(() => {
    loadLimits()
      .then(l => setLimits(l.redaction))
      .catch(() => {})
  }, [])
  useEffect(() => {
    if (file && artifact && !(VIEW_ONLY[artifact] && file.artifacts?.[artifact])) navigate(`/p/${id}/files/${fileId}`, { replace: true })
  }, [file, artifact, id, fileId, navigate])

  const guard = useUnsavedGuard({
    dirty,
    onSave: () => wb.current?.save() ?? false,
    onDiscard: () => wb.current?.discard()
  })
  const back = () => {
    const from = params.get('from')
    const fromHistoryKey = location.state?.fromHistoryKey
    if (from && (from === `/p/${id}` || from.startsWith(`/p/${id}?`) || from.startsWith(`/p/${id}/`))) {
      return navigate(from, fromHistoryKey ? { state: { restoreHistoryKey: fromHistoryKey } } : undefined)
    }
    const fallback = file?.folderId ? `/p/${id}?folder=${encodeURIComponent(file.folderId)}` : `/p/${id}`
    navigate(fallback)
  }
  function openMenu(e) {
    const button = e.currentTarget.getBoundingClientRect()
    const header = e.currentTarget.closest('header')?.getBoundingClientRect()
    setMenu({ top: (header ? header.bottom + TOOLBAR_HEIGHT : button.bottom) + 4, left: button.right })
  }

  async function apply() {
    setApplying(true)
    setError('')
    try {
      const { data: op } = await api.post(`/api/projects/${id}/apply`, { fileId })
      const result = await streamOperation(op.operationId)
      if (result.status === 'ERROR') throw new Error(result.error || 'applying the redactions failed')
      const fresh = await api.get(`/api/files/${fileId}`)
      setApplied(fresh.data)
      setDownloadError('')
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setApplying(false)
    }
  }

  async function run(kind, fn) {
    setMenu(null)
    setWorking(kind)
    setError('')
    try {
      await fn()
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setWorking('')
    }
  }
  const exportNow = () => run('export', () => exportAnnotated(file))
  const summaryNow = () => run('summary', () => downloadFile(file, 'reasons'))
  function importChosen(e, version) {
    const chosen = e.target.files?.[0]
    e.target.value = ''
    if (!chosen) return
    run('import', async () => {
      const res = await importAnnotated(fileId, chosen, version)
      if (res.stale) wb.current?.markStale(res.stale.currentVersion)
      else await wb.current?.reload()
    })
  }

  if (!file || !project) {
    return <div style={{ padding: 40 }}>{error ? <ErrorText data-testid="editor-error">{error}</ErrorText> : <Spinner size={22} />}</div>
  }
  if (!file.pdfObjectKey) {
    return (
      <div style={{ padding: 40 }}>
        <ErrorText data-testid="editor-error">Convert {file.name} to PDF first.</ErrorText>
      </div>
    )
  }
  const meta = [project.name, file.folderId ? folderPath(folders, file.folderId) : null].filter(Boolean).join(' · ')
  const badge = (
    <KindBadge
      kind={displayKind(file)}
      title={file.pdfObjectKey && file.kind !== 'PDF' && file.kind !== 'COMBINED' ? `Original format: ${file.kind}` : undefined}
      data-testid="editor-kind"
    />
  )
  const held = file.status === 'PROCESSING' || file.status === 'CONVERTING'
  const hasSummary = !!file.artifacts?.reasons

  if (VIEW_ONLY[artifact] && file.artifacts?.[artifact]) {
    return (
      <div style={{ height: '100vh' }}>
        <RedactionViewer
          tokens={tokens}
          ports={ports}
          pdfjs={pdfjs}
          title={file.name}
          badge={badge}
          meta={`${VIEW_ONLY[artifact].label} · ${meta}`}
          info={VIEW_ONLY[artifact].info}
          onBack={back}
          headerActions={() => (
            <SecondaryButton padding="short" onClick={() => downloadFile(file, artifact)} data-testid="download-btn">
              <DownloadOutlinedIcon sx={{ fontSize: 16 }} /> Download
            </SecondaryButton>
          )}
        />
      </div>
    )
  }
  return (
    <div style={{ height: '100vh' }}>
      <RedactionWorkbench
        ref={wb}
        tokens={tokens}
        ports={ports}
        pdfjs={pdfjs}
        limits={limits ?? undefined}
        title={file.name}
        badge={badge}
        meta={meta}
        onBack={back}
        onDirtyChange={(d, c) => {
          setDirty(d)
          setChanges(c)
        }}
        headerActions={({ dirty, busy, version, boxCount }) => (
          <>
            <SecondaryButton
              padding="short"
              onClick={apply}
              disabled={dirty || busy || applying || Boolean(working) || held}
              title={
                dirty
                  ? 'Save or discard your changes first'
                  : boxCount === 0
                    ? 'No boxes: the redacted copy is the document as it stands'
                    : 'Burn the boxes into a redacted copy'
              }
              data-testid="apply-btn">
              {applying ? <Spinner size={14} tone={tokens.colors.text.body} /> : <PublishedWithChangesOutlinedIcon sx={{ fontSize: 16 }} />}{' '}
              {applying ? 'Applying…' : 'Apply redactions'}
            </SecondaryButton>
            <SecondaryIconButton
              type="button"
              style={{ padding: 6, width: 33, height: 33 }}
              onClick={openMenu}
              disabled={applying || Boolean(working)}
              title="More actions"
              aria-label="More actions"
              data-testid="editor-menu">
              {working ? <Spinner size={14} tone={tokens.colors.text.body} /> : <MoreHorizIcon sx={{ fontSize: 18 }} />}
            </SecondaryIconButton>
            <ActionMenu
              anchorReference="anchorPosition"
              anchorPosition={menu ?? undefined}
              open={Boolean(menu)}
              onClose={() => setMenu(null)}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
              <MenuItem
                onClick={exportNow}
                title={boxCount === 0 ? 'Export the PDF without redaction annotations' : undefined}
                data-testid="export-annotated">
                <DownloadOutlinedIcon /> Export annotated PDF
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMenu(null)
                  importInput.current?.click()
                }}
                disabled={dirty || busy}
                title={dirty ? 'Save your changes first' : undefined}
                data-testid="import-annotated">
                <UploadFileOutlinedIcon /> Replace from annotated PDF…
              </MenuItem>
              <Divider />
              <span title={hasSummary ? undefined : 'Apply redactions first'} style={{ display: 'block' }}>
                <MenuItem onClick={summaryNow} disabled={!hasSummary} data-testid="download-reasons">
                  <DownloadOutlinedIcon /> Download summary
                </MenuItem>
              </span>
            </ActionMenu>
            <input
              ref={importInput}
              type="file"
              accept="application/pdf,.pdf"
              hidden
              onChange={e => importChosen(e, version)}
              data-testid="import-input"
            />
          </>
        )}
      />
      {error && (
        <ErrorText role="alert" data-testid="editor-error" style={{ position: 'fixed', left: 20, bottom: 12 }}>
          {error}
        </ErrorText>
      )}
      <ApplyResultDialog
        file={applied}
        downloading={downloadState}
        error={downloadError}
        onClose={() => {
          setApplied(null)
          back()
        }}
        onDownloadPackage={async () => {
          setDownloadState('package')
          setDownloadError('')
          try {
            await downloadRedactionPackage(applied)
          } catch (e) {
            setDownloadError(errorMessage(e))
          } finally {
            setDownloadState('')
          }
        }}
      />
      <UnsavedChangesDialog
        open={guard.open}
        tokens={tokens}
        fileName={file.name}
        changes={changes}
        saving={guard.saving}
        destination="the project"
        onKeepEditing={guard.keepEditing}
        onDiscard={guard.discard}
        onSaveAndLeave={guard.saveAndLeave}
      />
    </div>
  )
}
